import { Injectable } from '@angular/core';
import {
  CURRENCY_LIST,
  IMAGE_FARM_ORIGINAL_URL_REGEX,
  NUMBER_FORMAT_EXAMPLE_LIST,
  LOWERCASE_SYMBOL_TO_SUFFIX,
  UPPERCASE_SYMBOL_TO_SUFFIX,
  SYMBOL_TO_SUFFIX
} from '@fb-tool-front/overlay-editor/constants/overlay.constants';
import { Product, ProductCatalog } from '@fb-tool-front/shared/interfaces/catalogs.type';
import { tuiFormatNumber } from '@taiga-ui/core';
import {
  evaluateArithmeticOperator,
  getNestedProp
} from '@fb-tool-front/shared/utilities/utilities';

@Injectable({
  providedIn: 'root'
})
export class ProductFormatterService {
  public formatField(field, obj, product: Product): void {
    const fieldObject = this.getFieldObject();
    fieldObject[field]?.call(this, obj, product);
    obj.setCoords();
  }

  public getFormattedProductPrice(product: Product, priceField: string, message: string) {
    return product[priceField]
      ? product.currency === 'USD'
        ? `${CURRENCY_LIST.find(el => el.value === product.currency)?.currency}${tuiFormatNumber(
            parseFloat(product[priceField]),
            {
              decimalLimit: 2,
              decimalSeparator: '.',
              thousandSeparator: ' '
            }
          )}`
        : `${tuiFormatNumber(parseFloat(product[priceField]), {
            decimalLimit: 2,
            decimalSeparator: '.',
            thousandSeparator: ' '
          })} ${CURRENCY_LIST.find(el => el.value === product.currency)?.currency}`
      : message;
  }

  public parseProductFields(product: Product, catalog?: ProductCatalog): Product {
    product.images = product.images.map(image => {
      try {
        image = JSON.parse(image)?.url;
      } catch {}

      return image;
    });

    if (product.images.length === 0) {
      product.images = [product.image_url];
    }

    product.image_url_for_browser = product.images[0].replace(
      /(image\.aitarget\.com)|(img(-\w+?)?\.aitarget\.com)|(img(-\w+?)?\.aitarget\.tech)|(http:\/\/flixbus-sqr.lvlarm.com)/g,
      (match, p1, p2, p3, p4, p5, p6, offset, str) => {
        if (p6) {
          return 'https://image.aitarget.tech';
        }

        return 'image.aitarget.tech';
      }
    );
    product.additional_image_urls_for_browser = product.images.slice(1).length
      ? [
          ...product.images.slice(1).map(el =>
            el.replace(
              /(image\.aitarget\.com|img(-\w+?)?\.aitarget\.com|img(-\w+?)?\.aitarget\.tech|http:\/\/flixbus-sqr.lvlarm.com)/g,
              (match, p1, p2, p3, p4, p5, p6, offset, str) => {
                if (p6) {
                  return 'https://image.aitarget.tech';
                }

                return 'image.aitarget.tech';
              }
            )
          )
        ]
      : null;

    product.image_url = IMAGE_FARM_ORIGINAL_URL_REGEX.test(product.images[0])
      ? decodeURIComponent(product.images[0].replace(IMAGE_FARM_ORIGINAL_URL_REGEX, 'http'))
      : product.images[0];
    product.additional_image_urls = product.images
      .slice(1)
      .map(image =>
        IMAGE_FARM_ORIGINAL_URL_REGEX.test(image)
          ? decodeURIComponent(image.replace(IMAGE_FARM_ORIGINAL_URL_REGEX, 'http'))
          : image
      );

    product.price_for_browser = this.getFormattedProductPrice(product, 'price', 'price is empty');
    product.sale_price_for_browser = this.getFormattedProductPrice(
      product,
      'sale_price',
      'sale price is empty'
    );
    product.actual_price_for_browser = this.getFormattedProductPrice(
      product,
      'actual_price',
      'actual price is empty'
    );
    product.sale_value_for_browser = this.getFormattedProductPrice(
      product,
      'sale_value',
      'sale value is empty'
    );
    product.sale_percent_for_browser = this.getFormattedProductPrice(
      product,
      'sale_percent',
      'sale percent is empty'
    );

    product.brand = product.brand?.toString() || 'brand is empty';
    product.price = product.price?.toString() || 'price is empty';
    product.sale_price = product.sale_price?.toString() || 'sale price is empty';
    product.actual_price = product.actual_price?.toString() || 'actual price is empty';
    product.sale_value = product.sale_value?.toString() || 'sale value is empty';
    product.sale_percent = product.sale_percent?.toString() || 'sale percent is empty';
    product.product_type = product.product_type?.toString() || 'product type is empty';

    catalog?.product_fields?.forEach(field => {
      product[`${field.id}_for_browser`] = {
        price: this.getFormattedProductPrice(product, field.id, `${field.title} is empty`),
        number: product[field.id]?.toString() || `${field.title} is empty`,
        string: product[field.id]?.toString() || `${field.title} is empty`
      }[field.type];

      product[field.id] = product[field.id]?.toString() || `${field.title} is empty`;
    });

    return product;
  }

  private formatPrice(obj, product: Product): void {
    let text = product[obj.customField ? obj.customField.id : obj.productType];
    const numberFormat = NUMBER_FORMAT_EXAMPLE_LIST.find(item => item.value === obj.numberFormat);
    const { decimal, integer, intDelimiter, decDelimiter } = numberFormat.options;
    const additionalString = obj.textFilter === 'none' ? '' : obj.additionalText;

    if (obj.showArithmeticPrimitives && obj.arithmeticPrimitive) {
      text = String(
        evaluateArithmeticOperator(
          parseFloat(text),
          obj.arithmeticPrimitive.number,
          obj.arithmeticPrimitive.operator
        )
      );
    }

    text = this.getPriceFromString(text, decDelimiter);
    text = this.formatNumber(text, decimal, integer, intDelimiter, decDelimiter, obj.numberFormat);

    if (additionalString) {
      text = `${this.additionalStringFormatter()[obj.textFilter](text, additionalString)}`;
    }

    obj.origText = text;

    if (obj.asteriskAfterPrice) {
      text = obj.asteriskAfterPrice ? `${text}*` : text;
    } else if (obj.textBeforeAfterPrice) {
      if (obj.textBefore) {
        text = `${obj.textBefore}${text}`;
      }

      if (obj.textAfter) {
        text = `${text}${obj.textAfter}`;
      }
    }

    obj.set('text', text);
  }

  private formatTemperature(obj): void {
    let text = obj.text.replace(/[^-?\d*]/g, '');
    const additionalString = obj.textFilter === 'none' ? '' : obj.temperatureFormat;
    text = this.additionalStringFormatter()[obj.textFilter](text, additionalString);
    obj.set('text', text);
  }

  private additionalStringFormatter() {
    return {
      after: (text, additionalString) => text + additionalString,
      before: (text, additionalString) => additionalString + text,
      both: (text, additionalString) =>
        additionalString.split(';')[0] + text + additionalString.split(';')[1]
    };
  }

  private formatText(obj, product: Product): void {
    if (!obj.textFilter) {
      return;
    }

    const path = obj.customField ? obj.customField.id : obj.productType;
    let text = getNestedProp(product, path, ':') || path;
    obj.origText = text;

    if (obj.textBefore) {
      text = `${obj.textBefore}${text}`;
    }

    if (obj.textAfter) {
      text = `${text}${obj.textAfter}`;
    }

    const filterObject = this.getFilterObject(obj, product, text);
    filterObject[obj.textFilter].call(this);
  }

  private getFilterObject(obj, product: Product, text: string): any {
    return {
      none: () => {
        obj.set('text', text);
      },
      upper: () => {
        obj.set('text', text.toUpperCase());
      },
      lower: () => {
        obj.set('text', text.toLowerCase());
      },
      capitalize: () => {
        obj.set('text', this.toTitleCase(text));
      },
      title: () => {
        obj.set('text', this.stringToTitleCase(text));
      }
    };
  }

  private getFieldObject(): { [key: string]: any } {
    return {
      price: this.formatPrice,
      sale_price: this.formatPrice,
      actual_price: this.formatPrice,
      sale_value: this.formatPrice,
      sale_percent: this.formatPrice,
      name: this.formatText,
      custom_label_0: this.formatText,
      custom_label_1: this.formatText,
      custom_label_2: this.formatText,
      custom_label_3: this.formatText,
      custom_label_4: this.formatText,
      custom_number_0: this.formatText,
      custom_number_1: this.formatText,
      custom_number_2: this.formatText,
      custom_number_3: this.formatText,
      custom_number_4: this.formatText,
      brand: this.formatText,
      product_type: this.formatText,
      'address:region': this.formatText,
      'address:country': this.formatText,
      'address:city': this.formatText,
      temperature: this.formatTemperature,
      number: this.formatText,
      string: this.formatText
    };
  }

  private stringToTitleCase(str): string {
    return str.split(' ').map(this.toTitleCase).join(' ');
  }

  private toTitleCase(str: string): string {
    return str[0].toUpperCase() + str.substr(1).toLowerCase();
  }

  public formatNumber(str, decimal, integer, intDelimiter, decDelimiter, numberFormat): string {
    const re = '\\d(?=(\\d{' + (integer || 3) + '})+' + (decimal > 0 ? '\\D' : '$') + ')';
    let num = parseFloat(str).toFixed(Math.max(0, decimal));

    const isUppercaseDecimalSuffix = numberFormat >= 8 && numberFormat <= 11;
    const isLowercaseDecimalSuffix = numberFormat >= 12 && numberFormat <= 15;

    if (isUppercaseDecimalSuffix || isLowercaseDecimalSuffix) {
      const numArr = num.split('.');
      let int = numArr[0];
      let decimal = numArr[1]
        .split('')
        .map(symbol =>
          isUppercaseDecimalSuffix
            ? UPPERCASE_SYMBOL_TO_SUFFIX[symbol]
            : LOWERCASE_SYMBOL_TO_SUFFIX[symbol]
        )
        .join('');
      num = `${int}.${decimal}`;
    }

    return (decDelimiter ? num.replace('.', decDelimiter) : num).replace(
      new RegExp(re, 'g'),
      '$&' + intDelimiter
    );
  }

  public getPriceFromString(str: string, decDelimiter: string): string {
    if (!str) {
      return '';
    }

    const parsedString = str.replace(/[^0-9\.]/g, '');
    const parsedArray = parsedString.split('.').filter(item => item);
    const length = parsedArray.length;

    let integer = '0';
    let decimal = '00';

    if (length === 1) {
      integer = parsedString;
    }

    if (length === 2) {
      integer = parsedArray[0];
      decimal = parsedArray[1];
    }

    if (length > 2) {
      for (let i = 0; i < length - 1; i++) {
        integer += parsedArray[i];
      }
      decimal = parsedArray[length - 1];
    }

    return `${integer}.${decimal}`;
  }
}
